import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer';
import TWEEN from '@tweenjs/tween.js';

import { data } from './data';
import { coloursToArray } from '../../utils';
import './index.css';

import pathToModel from '../../assets/models/oceanfloor.glb';
import pathToTexture from '../../assets/img/textures/gray-circle.png';

export class SceneController {
  constructor(domRef) {
    this.domRef = domRef;

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.domElement.style.position = 'absolute';
    this.domRef.appendChild(this.renderer.domElement);
    this.cssRenderer = new CSS2DRenderer();
    this.cssRenderer.domElement.style.position = 'absolute';
    this.domRef.appendChild(this.cssRenderer.domElement);

    this.camera = this.setupCamera();
    this.controls = new OrbitControls(this.camera, this.cssRenderer.domElement);
    this.scene = new THREE.Scene();
    const axesHelper = new THREE.AxesHelper(5);
    this.scene.add(axesHelper);

    this.disposed = false;
    this.mesh = new THREE.Mesh();
    this.colourArr = (coloursToArray)('https://coolors.co/58355e-e03616-fff689-cfffb0-5998c5');
    this.gltfScene = new THREE.Group();
    this.loadingManager = new THREE.LoadingManager();
    this.raycaster = new THREE.Raycaster();
    this.isRaycast = false;
    this.labels = [];
    this.labelMarkers = [];

    this.setup();
  }

  setup() {
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.cssRenderer.setSize(window.innerWidth, window.innerHeight);
    //this.renderer.setClearColor(0x000000, 0); //transparent background
    this.scene.background = new THREE.Color(this.colourArr[0]);
    // this.scene.fog = new THREE.Fog(0x5C80BC, 50, 200);
    this.setupLight();
    this.setupControls();
    this.loadModels();
    this.setupCSSSubjects();
    // this.setupGeometry();
    this.render(); //render once on setup in useEffect hook
    console.log('SETUP DONE!');
  }

  setupCamera() {
    const VIEW_ANGLE = 50;
    const ASPECT_RATIO = window.innerWidth / window.innerHeight;
    const NEAR_PLANE = 1;
    const FAR_PLANE = 500;

    const camera = new THREE.PerspectiveCamera(VIEW_ANGLE, ASPECT_RATIO, NEAR_PLANE, FAR_PLANE);
    camera.position.set(0, 5, -16);
    //{x: -9.589030593318816, y: 0.44466707237190184, z: -14.43802708736732, isVector3: true}
    //x: 3.9062814281371927, y: 22.85989546064066, z: -74.57618046926063, isVector3: true}
    camera.lookAt(new THREE.Vector3(0, 0, 0));
    return camera;
  }

  setupLight() {
    const pointLight = new THREE.PointLight(this.colourArr[4], 0.5);
    pointLight.position.set(0.5, 0.5, 0);
    this.scene.add(pointLight);

    const pointLight1 = new THREE.PointLight(this.colourArr[2], 0.2);
    pointLight.position.set(-0.5, -0.5, 1);
    this.scene.add(pointLight1);

    const hemiLight = new THREE.HemisphereLight(this.colourArr[0], this.colourArr[4], 0.5);
    hemiLight.position.set(0, 0, 0);
    this.scene.add(hemiLight);

    const dirLight = new THREE.DirectionalLight(this.colourArr[1], 0.5);
    dirLight.position.set(0, 0.5, 0);
    this.scene.add(dirLight);
  }

  setupGeometry() {
    const geometry = new THREE.IcosahedronGeometry(0.5, 0);
    const material = new THREE.MeshPhongMaterial({
      color: this.colourArr[1],
      shininess: 10
    });
    const numElems = 20;
    for (let i = 0; i < 20; i++) {
      const mesh = new THREE.Mesh(geometry, material);
      const phi = Math.acos(- 1 + (2 * i) / numElems);
      const theta = Math.sqrt(numElems * Math.PI * 0.5) * phi;
      mesh.position.setFromSphericalCoords(10, phi, theta);
      this.scene.add(mesh);
    }
  }

  setupControls() {
    this.controls.zoomSpeed = 1.2;
    this.controls.panSpeed = 0.8;
    this.controls.enableZoom = true;
    this.controls.enablePan = true;
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.3;
    // this.controls.maxDistance = 400;
    // this.controls.minDistance = 100;
    this.controls.maxPolarAngle = 1.2737424473141494;
    this.controls.minPolarAngle = 1.2737424473141494;
  }

  loadModels() {
    // const target = new THREE.Vector3()
    const gltfLoader = new GLTFLoader();
    gltfLoader.load(pathToModel,
      (gltf) => {
        gltf.scene.scale.set(30, 30, 30);
        gltf.scene.rotateY(Math.PI * 0.2);
        gltf.scene.traverse(child => {
          if (child instanceof THREE.Mesh) {
            // child.castShadow = true;
            // child.receiveShadow = true;
            // child.material.metalness = 0.5;
            if (child.name === 'Mesh_4') {
              // this.gltfScene.updateMatrixWorld()
              console.log(child)
              // console.log(child.getWorldPosition(target))
            }
          }
        });
        this.scene.add(gltf.scene);
        this.gltfScene = gltf.scene;
      });
    console.log('Loading Complete!');
  }

  setupCSSSubjects() {
    const circleTexture = new THREE.TextureLoader().load(pathToTexture);
    for (let i = 0, l = data.length; i < l; i++) {
      const element = document.createElement('div');
      element.className = 'customSprite';
      element.innerHTML = i;
      element.addEventListener('click', () => {
        this.goToLabel(data[i]);
        const details = document.createElement('div');
        details.className = 'description';
        details.innerHTML = data[i].title; //+ '<br>' + `<img class='img' src='${data[i].imgArr[0]}'/>`;
        element.appendChild(details);
        details.addEventListener('click', (e) => this.onDescriptionClick(e))
      });

      const labelSpriteMaterial = new THREE.SpriteMaterial({
        map: circleTexture,
        depthTest: false,
        depthWrite: false,
        sizeAttenuation: false
      });
      const labelSprite = new THREE.Sprite(labelSpriteMaterial);
      labelSprite.scale.set(0.03, 0.03, 0.03);
      labelSprite.userData.id = data[i].id;
      labelSprite.position.copy(data[i].lookAt);
      // console.log(labelSprite);

      const objectCSS = new CSS2DObject(element);
      // console.log('ELEMENT: ', element, 'OBJECT3D: ', objectCSS);
      objectCSS.position.copy(data[i].lookAt);
      this.scene.add(labelSprite);
      this.scene.add(objectCSS);
      this.labelMarkers.push(labelSprite);
      this.labels.push(objectCSS);
    }
  }

  render() {
    this.renderer.render(this.scene, this.camera);
    this.cssRenderer.render(this.scene, this.camera);
  }

  update() {
    if (this.disposed) return;
    this.render();
    TWEEN.update();
  }

  goToLabel(label) {
    console.log(label, 'clicked');
    new TWEEN.Tween(this.camera.position)
      .to({
        x: label.camPos.x,
        y: label.camPos.y,
        z: label.camPos.z
      }, 500)
      .easing(TWEEN.Easing.Cubic.Out)
      .start()

    new TWEEN.Tween(this.controls.target)
      .to({
        x: label.lookAt.x,
        y: label.lookAt.y,
        z: label.lookAt.z
      }, 500)
      .easing(TWEEN.Easing.Cubic.Out)
      .start()
  }

  onKeyDown(e) {
    console.log('key down');
    console.log(this.camera.position);
  }

  onKeyUp(e) {
    console.log('key up');
  }

  onMouseClick(e) {
    if (this.isRaycast) {
      this.raycaster.setFromCamera({
        x: (e.clientX / this.renderer.domElement.clientWidth) * 2 - 1,
        y: -(e.clientY / this.renderer.domElement.clientHeight) * 2 + 1
      }, this.camera);

      const intersects = this.raycaster.intersectObjects(this.labelMarkers, true);
      //const intersects = this.raycaster.intersectObjects(this.gltfScene.children, true);
      if (intersects.length > 0) {
        console.log(intersects[0])
        if (intersects[0].object.userData && intersects[0].object.userData.id) {
          this.goToLabel(data[intersects[0].object.userData.id]);
          console.log(data[intersects[0].object.userData.id]);
        }
      }
    }
  }

  onDescriptionClick(e) {
    console.log('FIGURE OUT HOW TO TRIGGER REACT COMPONENTS');
  }

  dispose() {
    this.disposed = true;
    this.scene.children.forEach(child => {
      if (child instanceof THREE.Mesh) {
        child.material.dispose();
        child.geometry.dispose();
      }
    });
    this.renderer.dispose();
  }

  //TODO: optimise resize
  onWindowResize() {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.cssRenderer.setSize(window.innerWidth, window.innerHeight);
    this.render();
  }
}